import { Button, Chip, Progress, Switch, Tooltip } from "@heroui/react";
import { useEffect, useState } from "react";
import { api, fmtNumber } from "../lib/api.ts";
import { useLab, useRun } from "../lib/state.tsx";
import { Section, Stat } from "../components/Section.tsx";
import { TokenMeter, type Turn } from "../components/TokenMeter.tsx";

interface LoopResult {
  mode: "live" | "scripted";
  deferred: boolean;
  question: string;
  answer: string;
  turns: Turn[];
  totals: { schemaTokens: number; messageTokens: number; total: number; source: "api" | "estimate" };
}

interface ContextResult {
  full: LoopResult;
  deferred: LoopResult;
  comparison: {
    fullSchemaTokens: number;
    deferredSchemaTokens: number;
    savedTokens: number;
    savedPercent: number;
    source: "api" | "estimate";
    sameAnswer: boolean;
  };
}

export function ContextPanel() {
  const { modes, setMode } = useLab();
  const ctx = useRun<ContextResult>();
  const [counts, setCounts] = useState<{ total: number; listed: number } | null>(null);

  useEffect(() => {
    void api.catalog().then((c) => setCounts({ total: c.total, listed: c.listed }));
  }, []);

  const c = ctx.data?.comparison;
  const scaleMax = ctx.data
    ? Math.max(...ctx.data.full.turns.map((t) => t.schema.tokens + t.messages.tokens))
    : 1;

  return (
    <div className="flex flex-col gap-4">
      <Section
        title="What a catalogue costs, per request"
        blurb="Tool schemas are not sent once at connection time. They are part of the prompt, so they are re-sent on every single request for the whole conversation. With a catalogue this size that is the largest line on the bill — and most of it is for tools the conversation never touches."
      >
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Tools in catalogue" value={counts?.total ?? "—"} sub="core + synthetic" />
          <Stat
            label="Schema tokens / turn"
            value={c ? fmtNumber(Math.round(c.fullSchemaTokens / ctx.data!.full.turns.length)) : "—"}
            sub="full catalogue"
            tone="warning"
          />
          <Stat
            label="Schema tokens / turn"
            value={
              c ? fmtNumber(Math.round(c.deferredSchemaTokens / ctx.data!.deferred.turns.length)) : "—"
            }
            sub="deferred discovery"
            tone="success"
          />
          <Stat
            label="Saved"
            value={c ? `${c.savedPercent}%` : "—"}
            sub={c ? `${fmtNumber(c.savedTokens)} tokens over the conversation` : "run it"}
            tone="primary"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button color="primary" isLoading={ctx.pending} onPress={() => void ctx.run("context", { paceMs: 80 })}>
            Run the same question both ways
          </Button>
          <Switch
            size="sm"
            aria-label="serve deferred tools/list"
            isSelected={modes?.modes.deferred ?? false}
            onValueChange={(v) => void setMode("deferred", v)}
          >
            <span className="text-xs">
              Serve <code className="font-mono">tools/list</code> deferred to real clients too
            </span>
          </Switch>
          {c && (
            <Tooltip
              content={
                c.source === "api"
                  ? "Counted by Anthropic's count_tokens endpoint for the configured model."
                  : "Estimated from byte length — no API key is set. The ratio between the two runs is unaffected."
              }
              className="max-w-xs"
            >
              <Chip
                size="sm"
                variant="flat"
                color={c.source === "api" ? "success" : "warning"}
                className="cursor-help font-mono"
              >
                {c.source === "api" ? "measured" : "estimate"}
              </Chip>
            </Tooltip>
          )}
        </div>

        {ctx.error && <p className="mt-3 text-xs text-danger">{ctx.error}</p>}
      </Section>

      {ctx.data && (
        <>
          <Section
            title="Per turn, side by side"
            blurb="Both charts share one scale. The left bar stack is the same height on every turn because the whole catalogue is re-sent every time; the right one only grows when the model actually asks for a schema."
          >
            <div className="grid gap-6 lg:grid-cols-2">
              <TokenMeter
                turns={ctx.data.full.turns}
                scaleMax={scaleMax}
                label={`Full catalogue — ${fmtNumber(ctx.data.full.totals.schemaTokens)} schema tokens across ${ctx.data.full.turns.length} turns`}
                tone="primary"
              />
              <TokenMeter
                turns={ctx.data.deferred.turns}
                scaleMax={scaleMax}
                label={`Deferred discovery — ${fmtNumber(ctx.data.deferred.totals.schemaTokens)} schema tokens across ${ctx.data.deferred.turns.length} turns`}
                tone="success"
              />
            </div>

            <div className="mt-4">
              <div className="mb-1 flex items-center justify-between text-xs text-default-400">
                <span>Schema cost removed</span>
                <span className="font-mono">{ctx.data.comparison.savedPercent}%</span>
              </div>
              <Progress
                size="sm"
                color="success"
                aria-label="schema cost removed"
                value={ctx.data.comparison.savedPercent}
              />
            </div>
          </Section>

          <Section
            title="Did it still answer?"
            blurb="A saving that breaks the task is not a saving. The deferred run takes more turns — it has to look the tools up first — but it reaches the same answer."
          >
            <div className="flex flex-wrap items-center gap-2">
              <Chip
                size="sm"
                color={ctx.data.comparison.sameAnswer ? "success" : "danger"}
                variant="flat"
              >
                {ctx.data.comparison.sameAnswer ? "identical answer" : "answers differ"}
              </Chip>
              <Chip size="sm" variant="flat">
                {ctx.data.full.turns.length} turns → {ctx.data.deferred.turns.length} turns
              </Chip>
              <Chip size="sm" variant="flat" color={ctx.data.full.mode === "live" ? "success" : "warning"}>
                {ctx.data.full.mode}
              </Chip>
            </div>
            <p className="mt-3 max-w-3xl text-xs leading-relaxed text-default-300">
              <span className="text-default-500">Question — </span>
              {ctx.data.full.question}
            </p>
            <p className="mt-2 max-w-3xl text-xs leading-relaxed text-default-300">
              <span className="text-default-500">Answer — </span>
              {ctx.data.deferred.answer}
            </p>
          </Section>
        </>
      )}
    </div>
  );
}
